import * as React from "react"
import * as TabsPrimitives from "@radix-ui/react-tabs"
import { clx } from "../utils/clx"

/**
 * This component is based on the [Radix UI Tabs](https://radix-ui.com/primitives/docs/components/tabs) primitves.
 *
 * @excludeExternal
 */
const TabsRoot = (props: TabsPrimitives.TabsProps) => {
  return <TabsPrimitives.Root {...props} />
}
TabsRoot.displayName = "Tabs"

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitives.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitives.Trigger>
>(
  (
    {
      className,
      children,
      ...props
    },
    ref
  ) => (
    <TabsPrimitives.Trigger
      ref={ref}
      className={clx(
        "relative -mb-px px-3 pb-2 min-w-0 inline-flex items-center justify-start text-left truncate border-b-2 border-transparent outline-none transition-all",
        "txt-base-regular-plus text-ui-fg-subtle hover:text-ui-fg-base hover:border-grey-30",
        "disabled:bg-ui-bg-disabled disabled:text-ui-fg-muted",
        "focus-visible:bg-ui-bg-base focus:z-[1]",
        "data-[state=active]:border-blue-base data-[state=active]:text-blue-base",
        className
      )}
      {...props}
    >
      {children}
    </TabsPrimitives.Trigger>
  )
)
TabsTrigger.displayName = "Tabs.Trigger"

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitives.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitives.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitives.List
    ref={ref}
    className={clx("flex items-center", className)}
    {...props}
  />
))
TabsList.displayName = "Tabs.List"

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitives.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitives.Content>
>(({ className, ...props }, ref) => {
  return (
    <TabsPrimitives.Content
      ref={ref}
      className={clx("outline-none", className)}
      {...props}
    />
  )
})
TabsContent.displayName = "Tabs.Content"

const Tabs = Object.assign(TabsRoot, {
  Trigger: TabsTrigger,
  List: TabsList,
  Content: TabsContent,
})

export { Tabs }
